/**
 * availableModes.ts
 * ────────────────────────────────────────────────────────────────
 * Which practice surfaces a student can actually reach.
 *
 * Single source of truth for two consumers that must never disagree:
 *
 *   1. StudentTabBar — decides whether the "12ª" and "Admissão" tabs render.
 *   2. useOfflineReadiness / SubjectSelectPage — decide which modes to fill
 *      the offline cache for.
 *
 * They were previously independent. The tab bar had the predicate inline and
 * the offline fill hardcoded 'admission', so a student who could open the 12ª
 * tab got an offline cache containing only admission questions. Since the
 * cache is keyed `${subjectCode}:${mode}` and the read path filters by the
 * mode in the URL, the 12ª tab returned zero rows offline. Worse, a
 * 12ª-only student downloaded an admission pack whose every row their own
 * read path filtered out, spending cellular data for nothing.
 *
 * Keeping the rule here means adding a mode tab forces you through this file,
 * and the offline cache follows automatically.
 *
 * The `isCurrentGrade12` clause is easy to miss: a student whose
 * preferred_mode is 'admission' but who answered "Sim" to
 * "Estás na 12.ª classe agora?" still sees the 12ª tab. They are counted in
 * the admission bucket in analytics, so reasoning from preferred_mode alone
 * undercounts who needs 12ª content.
 */

export type PracticeMode = 'admission' | 'national_12'
export type PreferredMode = 'admission' | 'national_12' | 'both'

/**
 * Modes this student can open, in the order the tab bar shows them.
 *
 * Defaults to admission when preferred_mode is missing, matching the DB
 * column default and never widening the pool for a partially-shaped row.
 * Always returns at least one mode.
 */
export function availableModes(
  preferredMode: PreferredMode | string | null | undefined,
  isCurrentGrade12?: boolean | null,
): PracticeMode[] {
  const mode = preferredMode ?? 'admission'
  const out: PracticeMode[] = []
  if (mode === 'national_12' || mode === 'both' || isCurrentGrade12 === true) {
    out.push('national_12')
  }
  if (mode === 'admission' || mode === 'both') {
    out.push('admission')
  }
  // Defensive: an unrecognised preferred_mode with isCurrentGrade12 falsy
  // would otherwise yield an empty list and silently disable offline.
  return out.length > 0 ? out : ['admission']
}

/** Convenience for the common "should we bother with 12ª content" check. */
export function canAccessNational12(
  preferredMode: PreferredMode | string | null | undefined,
  isCurrentGrade12?: boolean | null,
): boolean {
  return availableModes(preferredMode, isCurrentGrade12).includes('national_12')
}
